import dayjs from 'dayjs';
import styled from 'styled-components';
import useTranslation from 'next-translate/useTranslation';

import { Biography } from '../../../../types/api';

import Text from '../../../../components/Text';

import { color, mq, space } from '../../../../stylesheets';

type ListEntryProps = Pick<
  Biography,
  'birthDate' | 'name' | 'deathDate' | 'deportationDate' | 'deportedTo'
> & {
  onClick: () => void;
};

const ListEntryDesktop = styled.button`
  width: 100%;
  text-align: left;
  padding: ${space('s')};
  box-shadow: 0 0 0 0 ${color('base')};
  transition: all 0.2s ease-in-out;
  position: relative;
  display: none;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  ${mq('m')} {
    display: flex;
  }

  &:hover {
    transform: scale(1.01);
    background: ${color('base', 50)};
    box-shadow: 0 0 5px 2px ${color('base', 50)};
  }

  p {
    text-align: left;
    width: 18.5%;
    padding-right: ${space('xxs')};

    &:nth-of-type(4) {
      width: 22%;
    }

    &:first-of-type {
      width: auto;
      flex-grow: 2;
    }
  }
`;

const ListEntryMobile = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: ${color('neutral', 10)};
  padding: ${space('s')} 0;

  ${mq('m')} {
    display: none;
  }

  & > * + * {
    margin-top: ${space('xxs')};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-left: ${space('xs')};
  }
`;

const ListDivider = styled.span`
  display: block;
  width: 100%;
  height: 5px;
  background: ${color('base')};

  ${mq('m')} {
    background: ${color('base', 50)};
  }
`;

const ListEntry = ({
  name,
  birthDate,
  deathDate,
  deportationDate,
  deportedTo,
  onClick
}: ListEntryProps) => {
  const { t } = useTranslation('common');

  return (
    <>
      <ListEntryDesktop type="button" onClick={onClick}>
        <Text aria-label={t('accessibility.biography.label', { name })}>{name}</Text>
        <Text
          aria-label={
            birthDate
              ? t('accessibility.biography.bornOn', {
                  date: dayjs(birthDate).format(t('dateFormat.short'))
                })
              : undefined
          }
        >
          {birthDate ? dayjs(birthDate).format(t('dateFormat.short')) : '–'}
        </Text>

        <Text
          aria-label={
            deathDate
              ? t('accessibility.biography.diedOn', {
                  date: dayjs(deathDate).format(t('dateFormat.short'))
                })
              : undefined
          }
        >
          {deathDate ? dayjs(deathDate).format(t('dateFormat.short')) : '–'}
        </Text>

        <Text
          aria-label={
            deportedTo
              ? t('accessibility.biography.deportedTo', { content: deportedTo.join(', ') })
              : undefined
          }
        >
          {deportedTo?.join(', ') || '–'}
        </Text>

        <Text
          aria-label={
            deportationDate
              ? t('accessibility.biography.deportedOn', {
                  date: dayjs(deportationDate).format(t('dateFormat.short'))
                })
              : undefined
          }
        >
          {deportationDate ? dayjs(deportationDate).format(t('dateFormat.short')) : '–'}
        </Text>
      </ListEntryDesktop>

      <ListEntryMobile type="button" onClick={onClick}>
        <ContentWrapper>
          <Text>{name}</Text>

          <ContentWrapper>
            <Text variant="copy-small">
              {birthDate ? dayjs(birthDate).format(t('dateFormat.short')) : t('unknown')}
            </Text>

            <Text variant="copy-small">
              {deathDate ? dayjs(deathDate).format(t('dateFormat.short')) : t('unknown')}
            </Text>
          </ContentWrapper>
        </ContentWrapper>

        <Text variant="copy-small" color="neutral" shade={50}>
          {t('biography.deportation', {
            deportedTo: deportedTo?.join(', ') || '–',
            deportationDate: deportationDate
              ? dayjs(deportationDate).format(t('dateFormat.short'))
              : '–'
          })}
        </Text>
      </ListEntryMobile>
      <ListDivider />
    </>
  );
};

export default ListEntry;
